import { Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import SelectMUI from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import i18next from "i18next";
import { Chip } from "@mui/material";
import { FIELD_REQUIRED, SELECT_A_OPTION } from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { getReactHookFormNestedError } from "../../utils/errors";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

/** Above SweetAlert alerts (`.bz-alert-container` uses z-index 9999). */
//eslint-disable-next-line
export const SELECT_MENU_ABOVE_MODAL_PROPS = {
  style: { zIndex: 10000 },
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8 * 2,
      zIndex: 10000,
    },
  },
};

const Select = ({
  dataTour = "",
  control,
  rules = { required: { value: true, message: i18next.t(FIELD_REQUIRED) } },
  errors = {},
  label = "",
  variant = "standard",
  MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 48 * 4.5 + 8 * 2,
        width: 250,
      },
    },
  },
  name = "",
  value = "",
  size = "medium",
  fullWidth = false,
  idLabel = `select-${name}`,
  options = [],
  displayEmpty = false,
  actionsChange = () => {},
  disabled = false,
  placeholder = "",
  showEmptyOption = false,
  sx = {},
  className = "",
  doTranslate = false,
  isRounded = false,
  multiple = false,
  useOnChange = true,
  backgroundColor = "",
  helperText = "",
  validationChange = async () => true,
  renderValue,
  renderTrailing = () => null,
}) => {
  const hiddenLabel = variant === "filled" && !label;

  const error = getReactHookFormNestedError(errors, name);

  const { t } = useTranslationApp();
  return (
    <>
      <Controller
        control={control}
        name={name}
        rules={rules}
        defaultValue={value}
        render={({ field }) => (
          <FormControl
            id={name}
            className={className}
            fullWidth={fullWidth}
            variant={variant}
            error={!!error}
            disabled={disabled}
            sx={{
              backgroundColor: "fields.background",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
              ...sx,
            }}
            data-tour={dataTour}
            hiddenLabel={hiddenLabel}
          >
            {label && <InputLabel id={idLabel}>{label}</InputLabel>}
            <SelectMUI
              size={size}
              placeholder={placeholder}
              labelId={idLabel}
              label={label}
              hiddenLabel={!label}
              displayEmpty={displayEmpty}
              multiple={multiple}
              renderValue={renderValue}
              {...field}
              value={options?.length > 0 ? field.value : ""}
              disabled={disabled}
              ref={field.ref}
              inputRef={field.ref}
              MenuProps={MenuProps}
              onChange={async (e) => {
                const isValid = await validationChange(e.target.value);
                if (!isValid) {
                  return;
                }

                if (useOnChange) {
                  field.onChange(e);
                }
                actionsChange(e.target.value);
              }}
              sx={{
                borderRadius: isRounded ? 4 : 1,
                backgroundColor,
                "& .MuiSelect-select": {
                  "& .MuiChip-root": {
                    display: "none",
                  },
                  "& .select-option-preview": {
                    display: "none",
                  },
                  "& .tooltip-modal": {
                    display: "none",
                  },
                },
              }}
            >
              {showEmptyOption && (
                <MenuItem
                  disabled
                  value=""
                  sx={{
                    px: {
                      xs: 1,
                      sm: 2,
                    },
                  }}
                >
                  <em>{t(SELECT_A_OPTION)}</em>
                </MenuItem>
              )}
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  title={option.tooltip || undefined}
                  sx={{
                    display: option.isHidden ? "none" : "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    minHeight: option.previewSrc !== undefined ? 96 : undefined,
                    py: option.previewSrc !== undefined ? 1 : undefined,
                    "&:hover": {
                      backgroundColor: "#E5E5E5",
                    },
                    px: {
                      xs: 1,
                      sm: 2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                      flex: 1,
                      gap: {
                        xs: 1,
                        sm: option.previewSrc !== undefined ? 1.5 : 2,
                      },
                    }}
                  >
                    {option.previewSrc !== undefined ? (
                      <Box
                        className="select-option-preview"
                        sx={{
                          width: 80,
                          height: 80,
                          flexShrink: 0,
                          borderRadius: 1.5,
                          overflow: "hidden",
                          backgroundColor: "#EEE",
                          ...(option.previewNode
                            ? {}
                            : {
                                backgroundImage: option.previewSrc
                                  ? `url(${option.previewSrc})`
                                  : "none",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                              }),
                        }}
                      >
                        {option.previewNode || null}
                      </Box>
                    ) : option.iconSrc ? (
                      <Box
                        component="img"
                        src={option.iconSrc}
                        alt=""
                        sx={{
                          width: 16,
                          height: 16,
                          objectFit: "contain",
                          display: "block",
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    {option.previewSrc !== undefined ? (
                      <Typography noWrap sx={{ minWidth: 0 }}>
                        {doTranslate ? t(option.label) : option.label}
                      </Typography>
                    ) : doTranslate ? (
                      t(option.label)
                    ) : (
                      option.label
                    )}
                    {option.chip && (
                      <Chip label={t(option.chip)} size="small" />
                    )}
                  </Box>
                  {renderTrailing({ option })}
                </MenuItem>
              ))}
            </SelectMUI>
          </FormControl>
        )}
      />
      {!!error && (
        <Typography variant="caption" color="error">
          {error?.message}
        </Typography>
      )}
      {!error && helperText && (
        <Typography variant="caption" color="textSecondary">
          {t(helperText)}
        </Typography>
      )}
    </>
  );
};

export default Select;
